import { useCallback, useEffect, useState } from "react";
import type { JobDto, MaterialStockDto, StockLocationDto } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { formatAmount } from "../lib/format.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { MaterialStockPanel } from "../components/inventory/materialStockPanel.js";
import { LocationDialog } from "../components/inventory/locationDialog.js";
import { cn } from "../lib/cn.js";
import { useI18n } from "../i18n/index.js";

export function MaterialsPage() {
  const { t } = useI18n();
  const [stock, setStock] = useState<MaterialStockDto[]>([]);
  const [locations, setLocations] = useState<StockLocationDto[]>([]);
  const [jobs, setJobs] = useState<JobDto[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l, j] = await Promise.all([
        api.listStock(),
        api.listStockLocations(),
        api.listJobs(),
      ]);
      setStock(s.items);
      setLocations(l.items);
      setJobs(j.items);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<MaterialStockDto>[] = [
    { key: "sku", header: t("common.fields.sku"), render: (r) => r.material.sku },
    {
      key: "name",
      header: t("common.fields.material"),
      render: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r.material.id)}
          className={cn(
            "font-medium text-action-primary-text hover:underline",
            selected === r.material.id && "font-semibold"
          )}
        >
          {r.material.name}
        </button>
      ),
    },
    {
      key: "unit",
      header: t("common.fields.unit"),
      render: (r) => r.material.unitOfMeasure,
    },
    {
      key: "onHand",
      header: t("inventory.column.onHand"),
      render: (r) => <span className="tabular">{r.totalOnHand}</span>,
    },
    {
      key: "reorder",
      header: t("inventory.column.reorder"),
      render: (r) =>
        r.reorderPointMinor !== null ? (
          <span className="tabular">{formatAmount(r.reorderPointMinor)}</span>
        ) : (
          "-"
        ),
    },
    {
      key: "status",
      header: t("common.fields.status"),
      render: (r) =>
        r.lowStock ? (
          <Badge tone="risk">{t("inventory.lowStock")}</Badge>
        ) : (
          <Badge tone="ready">{t("inventory.inStock")}</Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("nav.materials")}
        description={t("inventory.description")}
        actions={
          <Button variant="secondary" onClick={() => setLocationOpen(true)}>
            {t("inventory.addLocation")}
          </Button>
        }
      />

      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
        <DataTable
          caption={t("inventory.stockLevels")}
          rows={stock}
          rowKey={(r) => r.material.id}
          columns={columns}
          emptyTitle={t("inventory.emptyTitle")}
          emptyDescription={t("inventory.emptyDescription")}
        />
        {selected ? (
          <Card>
            <MaterialStockPanel materialId={selected} locations={locations} jobs={jobs} />
          </Card>
        ) : (
          <Card>
            <p className="text-sm text-ink-muted">{t("inventory.selectMaterial")}</p>
          </Card>
        )}
      </div>

      <LocationDialog
        open={locationOpen}
        onOpenChange={setLocationOpen}
        onCreated={() => void load()}
      />
    </>
  );
}
